import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RiArrowDownSLine, RiCheckLine, RiLockLine } from 'react-icons/ri';
import { C, LEVEL_COLORS } from '../../data/constants';
import { useAppConfig } from '../../context/ConfigContext';

/* JLPT level picker. Lifted out of the profile page so the app topbar can
   own it — the level you are studying is a global preference, not a
   property of one screen. */

/* Order only. Which of these are actually live comes from the admin
   panel at runtime — publishing N4 used to need a code change. */
export const LEVEL_ORDER = ['N5', 'N4', 'N3', 'N2', 'N1'];

export const LEVEL_META = {
  N5: { title: 'Beginner',           sub: 'Kana, 800 words, basic grammar' },
  N4: { title: 'Elementary',         sub: 'Everyday conversation & kanji' },
  N3: { title: 'Intermediate',       sub: 'Natural spoken Japanese' },
  N2: { title: 'Upper-intermediate', sub: 'Newspapers & real-world texts' },
  N1: { title: 'Advanced',           sub: 'Full academic & business fluency' },
};

export default function LevelSelect({ value, onChange, onOpenChange, compact = false }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const { config } = useAppConfig();
  const levels = LEVEL_ORDER.map(label => ({ label, available: config.levels?.[label] !== false }));

  const setOpenState = useCallback(next => {
    setOpen(next);
    onOpenChange?.(next);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => { if (!boxRef.current?.contains(e.target)) setOpenState(false); };
    const onKey = e => { if (e.key === 'Escape') setOpenState(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpenState]);

  const color = LEVEL_COLORS[value] || C.accent;

  return (
    <div className={`level-select${compact ? ' compact' : ''}`} ref={boxRef}>
      <button
        type="button"
        className={`level-select-trigger${open ? ' open' : ''}`}
        onClick={() => setOpenState(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ '--level-color': color }}
      >
        <span className="level-select-chip">{value}</span>
        <span className="level-select-text">
          <strong>{LEVEL_META[value]?.title}</strong>
          <span>{LEVEL_META[value]?.sub}</span>
        </span>
        <RiArrowDownSLine className="level-select-caret" size={18} />
      </button>

      {open && (
        <div className="level-select-menu anim-pop" role="listbox" aria-label="JLPT level">
          {levels.map(({ label, available }) => {
            const c = LEVEL_COLORS[label];
            const selected = label === value;
            return (
              <button
                key={label}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={!available}
                className={`level-select-option${selected ? ' selected' : ''}${available ? '' : ' locked'}`}
                onClick={() => { if (available) { onChange?.(label); setOpenState(false); } }}
                style={{ '--level-color': c }}
              >
                <span className="level-select-chip">{label}</span>
                <span className="level-select-text">
                  <strong>{LEVEL_META[label]?.title}</strong>
                  <span>{available ? LEVEL_META[label]?.sub : 'Not live yet — coming soon'}</span>
                </span>
                {available
                  ? (selected && <RiCheckLine size={16} className="level-select-tick" />)
                  : <RiLockLine size={14} className="level-select-lock" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
